import moment from "moment";
import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import Config from "@hero/shared/models/config";
import { IOffDaysWfhDays } from "@hero/shared/models/offDaysWfhDays";
import { IUserInfo } from "@hero/shared/models/userInfo";
import { IWfhDayOffDay } from "@hero/shared/models/wfhDayoffDayRegister";
import { IWorkingTime } from "@hero/shared/models/workingTime";
import { roleForPoint } from "@hero/shared/utils";
import {
    AttendanceCellStatus,
    classifyAttendanceCell,
    isOffDay,
    listDayKeys,
    toDayKey,
} from "@hero/shared/utils/working-time-upload";
import { IWorkingTimeUploadState } from "../../../redux/reducers/workingTimeUpload.reducer";
import "./styles.scss";

const CELL_LABEL: Record<AttendanceCellStatus, string> = {
    ok: "Đúng giờ",
    late: "Đi muộn",
    missingPunch: "Thiếu chấm công",
    leave: "Nghỉ phép",
    off: "Ngày nghỉ",
    noData: "Không có dữ liệu",
    future: "Tương lai",
};
const WEEKDAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

const WorkingTimeGrid: FC<{ startTime: string; endTime: string; filteredUserIDs: string[] }> = ({
    startTime,
    endTime,
    filteredUserIDs,
}) => {
    const allUsers: IUserInfo[] = useSelector((state: any) => state.userInfoState.userInfos);
    const { records, holidays, complementDays }: IWorkingTimeUploadState = useSelector(
        (state: any) => state.workingTimeUpload
    );
    const offDaysWfhDays: IOffDaysWfhDays[] = useSelector((state: any) => state.workingTime.offDaysWfhDays);
    const todayKey = toDayKey(new Date());

    const dayKeys = useMemo(
        () => (startTime && endTime && startTime <= endTime ? listDayKeys(startTime, endTime) : []),
        [startTime, endTime]
    );
    const users = useMemo(
        () =>
            allUsers.filter(
                (u) =>
                    roleForPoint(u) &&
                    u.status == Config.STATUS_PUBLIC &&
                    (!filteredUserIDs.length || filteredUserIDs.includes(u._id))
            ),
        [allUsers, filteredUserIDs]
    );
    const recordByKey = useMemo(() => {
        const map: Record<string, IWorkingTime> = {};
        records.forEach((r) => (map[`${r.userId}_${toDayKey(r.date)}`] = r));
        return map;
    }, [records]);
    const leaveByKey = useMemo(() => {
        const map: Record<string, IWfhDayOffDay[]> = {};
        offDaysWfhDays.forEach((u) =>
            u.offDays.forEach((d) => {
                const key = `${u.userId}_${toDayKey(d.date)}`;
                (map[key] = map[key] ?? []).push(d);
            })
        );
        return map;
    }, [offDaysWfhDays]);
    const offByDay = useMemo(() => {
        const map: Record<string, boolean> = {};
        dayKeys.forEach((k) => (map[k] = isOffDay(k, holidays, complementDays)));
        return map;
    }, [dayKeys, holidays, complementDays]);

    const renderCell = (userId: string, dayKey: string) => {
        const record = recordByKey[`${userId}_${dayKey}`];
        const leaves = leaveByKey[`${userId}_${dayKey}`] ?? [];
        const status = classifyAttendanceCell({
            record,
            leaveSlots: leaves.map((l) => l.slot),
            isOff: offByDay[dayKey],
            isFuture: dayKey > todayKey,
        });
        const text = record ? `${record.checkin || "--"} – ${record.checkout || "--"}` : "";
        const tooltip = [CELL_LABEL[status], text, ...leaves.map((l) => `Nghỉ ${l.slot}${l.reason ? ": " + l.reason : ""}`)]
            .filter(Boolean)
            .join(" · ");
        return (
            <td key={dayKey} className={`wtg-cell ${status}`} title={tooltip}>
                {text}
            </td>
        );
    };

    return (
        <div className="working-time-grid">
            <div className="wtg-scroll">
                <table>
                    <thead>
                        <tr>
                            <th className="wtg-sticky">Nhân viên</th>
                            {dayKeys.map((k) => {
                                const m = moment(k, "YYYY-MM-DD");
                                return (
                                    <th key={k} className={offByDay[k] ? "off" : ""}>
                                        {m.format("DD/MM")}
                                        <br />
                                        {WEEKDAYS[m.isoWeekday() - 1]}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u._id}>
                                <td className="wtg-sticky">
                                    {u.lastName} {u.firstName}
                                </td>
                                {dayKeys.map((k) => renderCell(u._id, k))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="wtg-legend">
                {(Object.keys(CELL_LABEL) as AttendanceCellStatus[]).map((s) => (
                    <span key={s}>
                        <i className={`wtg-cell ${s}`} /> {CELL_LABEL[s]}
                    </span>
                ))}
            </div>
        </div>
    );
};

export default WorkingTimeGrid;
